import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '@rydres/api-client';
import { driverApi } from './api.js';
import type { Profile } from './types.js';

interface ProfileState {
  profile: Profile | null;
  /** Set when the last load failed; screens show it instead of "Loading…" forever. */
  error: string | null;
  reload: () => Promise<Profile | null>;
  save: (patch: Record<string, unknown>) => Promise<Profile>;
}
const Ctx = createContext<ProfileState | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    // A deploy or a flaky connection must not leave the app stuck: retry a few times, then say so.
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const p = await driverApi.profile();
        setProfile(p);
        setError(null);
        return p;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) break;
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    }
    setError('Could not load your profile. Check your connection and try again.');
    return null;
  }, []);
  const save = useCallback(async (patch: Record<string, unknown>) => {
    const p = await driverApi.updateProfile(patch);
    setProfile(p);
    return p;
  }, []);
  useEffect(() => void reload(), [reload]);
  const value = useMemo(() => ({ profile, error, reload, save }), [profile, error, reload, save]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useProfile(): ProfileState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useProfile outside provider');
  return v;
}

export const MISSING_LABELS: Record<string, { label: string; to: string }> = {
  handle: { label: 'Choose your page name', to: '/settings/profile' },
  displayName: { label: 'Add your name', to: '/settings/profile' },
  legalName: { label: 'Add your business legal name', to: '/settings/profile' },
  tcpNumber: { label: 'Add your TCP number', to: '/settings/profile' },
  phonePublic: { label: 'Add the number riders can call', to: '/settings/profile' },
  vehicle: { label: 'Add your vehicle', to: '/settings/vehicle' },
  services: { label: 'Add at least one service with a price', to: '/services' },
  stripe: { label: 'Set up card payments', to: '/settings/payments' },
  plan: { label: 'Start your subscription', to: '/settings/billing' },
  photo: { label: 'Add your photo: riders see it on every ride', to: '/settings/profile' },
};

/** Standard placeholder for screens that need the profile first. */
export function ProfileLoading() {
  const { error, reload } = useProfile();
  if (!error) return <p className="p-4 text-sm text-muted">Loading…</p>;
  return (
    <div className="space-y-3 p-4">
      <p className="text-sm text-danger">{error}</p>
      <button onClick={() => void reload()} className="rounded-xl bg-card2 px-4 py-2 text-sm font-bold">Try again</button>
    </div>
  );
}
